import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import { angleDelta, lerpOrSnap, polarToVec } from "../../core/util/MathUtil";
import {
  createRegularPolygon,
  scalePolygon,
} from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import { applyMovingRainbow, VectorFilter } from "../graphics";
import VectorSprite from "../graphics/VectorSprite";
import { Hittable } from "../Hittable";
import {
  BaseMunition,
  ExplosionConfig,
  MunitionMovement,
  MunitionType,
  MunitionVisualConfig,
} from "./BaseMunition";
import { GravityMovement } from "./movements/GravityMovement";

// BigBomb physics constants
const BIG_BOMB_MASS = 3;
const INITIAL_VELOCITY_MULTIPLIER = 1;

// BigBomb rendering constants
const BIG_BOMB_STROKE_WIDTH = 1.5;
const BIG_BOMB_ROTATION_LERP = 0.15; // Smooth rotation interpolation factor

// BigBomb explosion constants
const BIG_BOMB_EXPLOSION_RADIUS = 50;
const BIG_BOMB_EXPLOSION_LIFETIME = 2.0; // seconds
const BIG_BOMB_DAMAGE = 2;

// BigBomb health system
const BIG_BOMB_MAX_HEALTH = 10;

const BOMB_POLYGON = scalePolygon(
  [
    V(-3, -2), // far corner of tail
    V(-2, -2), // tail
    V(-1, -1), // tail
    V(0, -0.75), // connector
    V(1, -2), // head
    V(3, -2), // head
    V(4, -1), // head
    V(4, 1), // head
    V(3, 2), // head
    V(1, 2), // head
    V(0, 0.75), // connector
    V(-1, 1), // tail
    V(-2, 2), // tail
    V(-3, 2), // tail
  ],
  2,
);

export class BigBomb extends BaseMunition implements Hittable {
  munitionType = MunitionType.BIG_BOMB;
  damage = BIG_BOMB_DAMAGE;

  private renderer!: VectorSprite;
  private gravityMovement!: GravityMovement;

  // Health system for BigBomb
  private currentHealth = BIG_BOMB_MAX_HEALTH;

  constructor(position: V2d) {
    super(position);
    this.tags = ["bigBomb", "bomb", "levelEntity"]; // Include "bomb" for compatibility
  }

  protected createBody(position: V2d): Body {
    const body = new Body({ mass: BIG_BOMB_MASS });
    body.fromPolygon(BOMB_POLYGON, {
      optimalDecomp: false,
      removeCollinearPoints: false,
    });

    for (const shape of body.shapes) {
      shape.collisionGroup = collisionGroups("Bomb");
      shape.collisionMask = collisionGroups("Turret", "Bullet", "Explosion");
    }

    body.position = [position.x, position.y];

    // Add slower initial velocity toward center
    const directionToCenter = V(0, 0)
      .sub(position)
      .normalize()
      .imul(INITIAL_VELOCITY_MULTIPLIER);
    body.velocity = [directionToCenter.x, directionToCenter.y];

    return body;
  }

  protected setupVisual(): void {
    const visualConfig = this.getVisualConfig();
    this.renderer = VectorSprite.fromPolygon(
      visualConfig.points,
      visualConfig.strokeColor,
      visualConfig.strokeWidth || 1,
    );

    // Add moving rainbow color filter to make BigBomb distinctive
    this.renderer.addFilter(
      new VectorFilter(applyMovingRainbow(undefined, 2.0)),
    );

    this.addChild(this.renderer);
  }

  getMovementBehavior(): MunitionMovement {
    this.gravityMovement = new GravityMovement();
    return this.gravityMovement;
  }

  getVisualConfig(): MunitionVisualConfig {
    // Create precise 14-segment bomb shape: 7 nose + 2 body + 5 tail

    return {
      points: BOMB_POLYGON,
      strokeColor: Colors.WHITE,
      strokeWidth: BIG_BOMB_STROKE_WIDTH,
    };
  }

  getExplosionConfig(): ExplosionConfig {
    return {
      radius: BIG_BOMB_EXPLOSION_RADIUS,
      lifetime: BIG_BOMB_EXPLOSION_LIFETIME,
      color: Colors.PINK,
      damage: this.damage,
    };
  }

  protected updateVisualPosition(): void {
    if (this.body && this.renderer) {
      this.renderer.setPosition(this.body.position);

      // Smooth rotation toward movement direction
      const velocity = V(this.body.velocity);
      if (velocity.magnitude > 0.1) {
        const targetAngle = velocity.angle;
        const angleDiff = angleDelta(this.body.angle, targetAngle);

        // Smoothly interpolate rotation
        this.body.angle = lerpOrSnap(
          this.body.angle,
          this.body.angle + angleDiff,
          BIG_BOMB_ROTATION_LERP,
          0.01, // Snap threshold in radians
        );

        this.renderer.setRotation(this.body.angle);
      }
    }
  }

  protected getExplosionType(): "bigBomb" {
    return "bigBomb";
  }

  // Override takeHit to implement health system
  takeHit(damage: number) {
    this.currentHealth -= damage;

    if (this.currentHealth <= 0) {
      this.explode();
    } else {
      // Dispatch impact event for non-fatal hit
      this.dispatch("bulletImpact", {
        targetType: "bigBomb" as const,
        position: this.getPosition(),
        damage: damage,
        remainingHealth: this.currentHealth,
      });
    }
  }

  /**
   * Get the renderer for applying visual effects
   */
  getRenderer(): VectorSprite {
    return this.renderer;
  }
}
